// Recommended image size per Customer CMS slot. Sizes come from how brugo.in / the
// customer app actually render each image (box size × 2 for sharp phone/retina screens).
// spec: { w, h, ratio (label), maxKB, formats, note }

const S = (w, h, ratio, maxKB, note, formats = "JPG or WebP") => ({ w, h, ratio, maxKB, note, formats });

export const SPECS = {
  hero: S(1920, 1080, "16:9", 600, "Full-width hero at the top of the home page. Keep faces and text away from the edges — it is cropped on phones."),
  promoFull: S(2400, 900, "8:3", 500, "Big promotion banner (shows 480 px tall on desktop, 360 px on phones). Leave the left third calm for the headline."),
  promoStrip: S(2400, 320, "15:2", 300, "Thin promotion strip above raw materials. Very wide — keep the subject in the middle."),
  beforeAfter: S(1600, 1000, "16:10", 500, "Use the SAME size and camera angle for the raw and the finished photo, or the slider won't line up."),
  tile: S(1200, 900, "4:3", 400, "Card image (shows about 220 px tall). Centre the subject."),
  step: S(1200, 900, "4:3", 400, "Step / journey card image."),
  poster: S(1280, 720, "16:9", 300, "Still shown before a testimonial video plays."),
  person: S(400, 400, "1:1", 150, "Square photo of the person — face centred."),
  logo: S(512, 512, "1:1", 100, "Logo or icon on a transparent background.", "PNG or SVG"),
  appHero: S(1080, 608, "16:9", 400, "Customer app home hero."),
  appBanner: S(1260, 540, "21:9", 300, "Customer app carousel banner."),
  band: S(2400, 1000, "12:5", 500, "Full-width photo band with two lines of text over the bottom-left. Keep that corner calm."),
  generic: S(1600, 1000, "16:10", 500, "General image."),
};

// Pick the spec from where the field sits, e.g. "home.promoTop.items.0.image".
export function specFor(path = "", key = "") {
  const p = String(path).toLowerCase();
  const k = String(key).toLowerCase();
  if (/logo|icon/.test(k)) return SPECS.logo;
  if (/avatar|person|portrait|face|profile/.test(k)) return SPECS.person;
  if (/poster/.test(k)) return SPECS.poster;
  if (p.includes("promomaterials") || p.includes("strip")) return SPECS.promoStrip;
  if (p.includes("promo")) return SPECS.promoFull;
  if (p.includes("beforeafter") || /raw|done/.test(k)) return SPECS.beforeAfter;
  if (p.includes("testimonial")) return SPECS.person;
  if (p.includes("photoband") || k === "band") return SPECS.band;
  if (/^trade\d$/.test(k) || p.includes("onsite")) return SPECS.tile;
  if (p.startsWith("hero.") || p.includes(".hero.")) return SPECS.hero;
  if (p.includes("journey") || p.includes("steps") || p.includes("howitworks")) return SPECS.step;
  if (p.includes("tiles") || p.includes("doorway") || p.includes("cards") || p.includes("items")) return SPECS.tile;
  return SPECS.generic;
}

export const fmtKB = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
export const HARD_MAX_BYTES = 5 * 1048576; // refuse anything above 5 MB

// Checks a picked file against the spec. Returns { problems: [..], ok: [..] }.
export function checkImage(spec, { width, height, bytes }) {
  const problems = [];
  if (bytes > HARD_MAX_BYTES) problems.push(`File is ${fmtKB(bytes)} — the limit is 5 MB.`);
  else if (bytes > spec.maxKB * 1024) problems.push(`File is ${fmtKB(bytes)} — aim for under ${spec.maxKB} KB so the page loads fast.`);
  if (width && height) {
    const want = spec.w / spec.h, got = width / height;
    if (Math.abs(got - want) / want > 0.08) problems.push(`Shape is ${width}×${height} — expected ${spec.ratio}. It will be cropped.`);
    if (width < spec.w * 0.7) problems.push(`Only ${width} px wide — recommended ${spec.w} px, it may look blurry.`);
  }
  return problems;
}
